import { useEffect, useState } from "react";
import { Target, Lightbulb } from "lucide-react";
import { api, money } from "../lib/api";
import { PageHeader, Panel } from "../components/common";
import { Integrations, Downloads, Payout } from "../components/merchant/PortalHub";

const Stat = ({ k, v, c, sub, testid }) => (
  <div className="p-5 border-r border-b border-line" data-testid={testid}>
    <p className="label-xs">{k}</p><p className="font-mono text-2xl font-extrabold mt-2 tabular" style={{ color: c }}>{v}</p>
    {sub && <p className="font-mono text-[10px] text-white/35 mt-1">{sub}</p>}
  </div>
);

export default function MerchantPortal() {
  const [m, setM] = useState(null);
  useEffect(() => { api.get("/merchant/overview").then((r) => setM(r.data)); }, []);
  if (!m) return <p className="font-mono text-sm text-white/35">loading portal…</p>;
  const rv = m.revenue;
  return (
    <div data-testid="merchant-portal">
      <PageHeader eyebrow={`// merchant portal · ${m.merchant}`} title="Your Hub" />
      <p className="label-xs mb-3">Your stats</p>
      <div className="grid grid-cols-2 md:grid-cols-5 border-l border-t border-line bg-surface">
        <Stat k="Disputes" v={m.disputes} c="#fff" sub={`${m.open} open`} testid="mp-disputes" />
        <Stat k="Win rate" v={`${m.win_rate}%`} c="var(--brand)" testid="mp-win-rate" />
        <Stat k="Recovered all-time" v={money(m.recovered_total)} c="var(--brand)" testid="mp-recovered" />
        <div className="p-5 border-r border-b border-line md:col-span-2" data-testid="mp-target">
          <p className="label-xs flex items-center gap-2"><Target className="w-3 h-3" /> Recovered this month vs target</p>
          <p className="font-mono text-2xl font-extrabold mt-2"><span className="text-brand">{money(m.recovered_month)}</span> <span className="text-white/35 text-sm">/ {money(m.monthly_target)}</span></p>
          <div className="h-1.5 bg-white/5 mt-3"><div className="h-full" style={{ width: `${Math.min(m.target_pct, 100)}%`, background: "linear-gradient(90deg,var(--accent2),var(--brand))" }} /></div>
          <p className="font-mono text-[10px] text-white/40 mt-1">{m.target_pct}% of target</p>
        </div>
      </div>
      <div className="grid md:grid-cols-3 gap-6 mt-6">
        <Integrations />
        <Downloads />
        <Payout />
      </div>
      <div className="grid grid-cols-12 gap-6 mt-6">
        <Panel title="Recommendations to reduce chargebacks" className="col-span-12 xl:col-span-7" testid="mp-recommendations">
          <ul className="space-y-4">
            {m.recommendations.map((r, i) => (
              <li key={i} className="flex gap-3" data-testid={`mp-rec-${i}`}>
                <Lightbulb className="w-4 h-4 text-brand shrink-0 mt-0.5" />
                <div><p className="text-sm text-white">{r.title}</p><p className="text-xs text-white/55 mt-0.5">{r.action}</p>
                  {r.impact != null && <p className="font-mono text-[10px] text-[#FFB800] mt-1">{money(r.impact)} disputed in this segment</p>}</div>
              </li>
            ))}
          </ul>
        </Panel>
        <Panel title="Platform revenue & margin · this merchant" className="col-span-12 xl:col-span-5" testid="mp-revenue">
          <dl className="space-y-3 font-mono text-xs">
            {[["Revenue share", `${Math.round(rv.rev_share_pct * 100)}% of recovered + prevented`], ["Platform revenue", money(rv.platform_revenue)],
              ["Labor cost", money(rv.labor_cost)], ["Intercept alert fees", money(rv.alert_fees)], ["AI drafting cost", money(rv.ai_cost)],
              ["Total platform cost", money(rv.platform_cost)], ["Processor fees (merchant-paid)", money(rv.processor_fees_paid_by_merchant)],
              ["Cost per recovery", rv.cost_per_recovery != null ? money(rv.cost_per_recovery) : "—"]].map(([k, v]) => (
              <div key={k} className="flex justify-between border-b border-white/5 pb-2"><dt className="text-white/50">{k}</dt><dd className="text-white">{v}</dd></div>
            ))}
            <div className="flex justify-between pt-1"><dt className="text-white">Margin</dt><dd className="text-brand text-lg font-extrabold" data-testid="mp-margin">{money(rv.margin)} · {rv.margin_pct}%</dd></div>
          </dl>
          <p className="text-[11px] text-white/35 mt-3">Single-merchant workspace today. Per-tenant isolation and multi-merchant roll-ups need the multi-tenant + login upgrade.</p>
        </Panel>
      </div>
    </div>
  );
}
